/**
 * `field` template — a card-sized, non-interactive rendering of the portfolio field: x = log
 * velocity (with the zero-velocity gutter), y = value (Rx) / inventory (Dx), baseline nodes
 * neutral, each highlight cohort painted per its `emphasis`.
 *
 * Deliberately NOT the full 1,650-line interactive engine (`field/plot.ts`) — this reuses its
 * pure geometry (`toLx`, `inGutter`, `gutterFrac` from `plot-math.ts`) and the same domain
 * (`FIELD_SCALE`), but draws plain SVG circles rather than the canvas sprite/cluster pipeline.
 * Baseline nodes are capped and deterministically sampled on a real (~9k SKU) drop so a card
 * never renders thousands of DOM nodes; every highlighted SKU is always drawn.
 *
 * Hovering the nearest rendered point (a small-radius hit test, scanned over the same capped set
 * that's actually drawn — at most a couple thousand points, not the full ~9k-SKU cohort) shows
 * that SKU's detail. `plot-math.ts`'s own `nearest()` is built for the full interactive engine's
 * bucketed spatial index over the WHOLE uncapped cohort; reusing it here would mean building a
 * matching `Grid`/`Transform` for a point set this component already caps well below the size
 * that hit test exists to make fast, so a direct scan over the rendered points is the simpler,
 * equally-fast fit for a preview this size.
 */

import { type MouseEvent, useId, useRef, useState } from "react";

import { WL } from "@/features/sku-velocity/field/lenses";
import { gutterFrac, inGutter, toLx } from "@/features/sku-velocity/field/plot-math";
import { fmtVel } from "@/features/sku-velocity/field/util";
import type { FieldView, Highlight } from "@/lib/api";
import { FIELD_SCALE, fmtMoneyShort, type Sku } from "@/lib/data";
import { evalCohort } from "@/lib/specs/evaluate";

import { AXIS, AXIS_LABEL, logTicks, spacedTicks } from "./axis";
import { AxisTitle } from "./axis-title";
import { ChartTooltip } from "./chart-tooltip";
import { HIGHLIGHT_PRIMARY, HIGHLIGHT_SECONDARY, NEUTRAL } from "./colors";
import { useChartSize } from "./use-chart-size";
import { useChartZoom } from "./use-chart-zoom";
import type { ViewChrome, ViewSize } from "./view-shell";
import { ZoomControls } from "./zoom-controls";

const sq = Math.sqrt;

/** Baseline dots drawn on a card before sampling kicks in. Every highlighted SKU is exempt. */
const MAX_BASELINE = { card: 800, expanded: 2500 } as const;
/** Hit-test radius, in CSS pixels. */
const HOVER_RADIUS = { card: 8, expanded: 7 } as const;
/** Below this plot width the gutter's label is abbreviated rather than left to collide with
 *  the first velocity tick. */
const WIDE_ENOUGH_FOR_FULL_LABELS = 260;

function passesFilters(sku: Sku, filters: NonNullable<FieldView["filters"]>): boolean {
  if (filters.type !== "all" && sku.type !== filters.type) {
    return false;
  }
  if (filters.search && !sku.name.toLowerCase().includes(filters.search.toLowerCase())) {
    return false;
  }
  return true;
}

function highlightColor(emphasis: Highlight["emphasis"]): string {
  return emphasis === "secondary" ? HIGHLIGHT_SECONDARY : HIGHLIGHT_PRIMARY;
}

interface FieldChartProps {
  spec: FieldView;
  size: ViewSize;
  skus: readonly Sku[];
  chrome?: ViewChrome;
}

export function FieldChart({ spec, size, skus, chrome = "full" }: FieldChartProps) {
  const [hoverId, setHoverId] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const box = useChartSize();
  // A ~9k-SKU scatter piles up at card size, so this one is zoomable: ⌘/ctrl+scroll or the
  // corner controls, drag to pan, double-click to reset.
  const zoomControls = useChartZoom({ width: box.width, height: box.height });
  const { zx, zy } = zoomControls;
  // `useId` returns colons, which are legal in an id but awkward inside `url(#…)` — strip them.
  const clipId = `field-clip-${useId().replace(/:/g, "")}`;
  const filters = spec.filters ?? { type: "all", search: "" };
  const filtered = skus.filter((s) => passesFilters(s, filters));

  // Later highlights paint over earlier ones, same as the field's own draw order.
  const colorById = new Map<number, string>();
  for (const h of spec.highlights ?? []) {
    for (const s of evalCohort(h.cohort, filtered)) {
      colorById.set(s.id, highlightColor(h.emphasis));
    }
  }

  const velMax = filters.type === "Dx" ? FIELD_SCALE.dxVelMax : FIELD_SCALE.velMax;
  const rxMax = toLx(velMax) || 1;
  const fracOf = (s: Sku) =>
    s.type === "Dx"
      ? sq(Math.min(FIELD_SCALE.invMax, Math.max(0, s.inv))) / sq(FIELD_SCALE.invMax)
      : Math.min(1, sq(Math.max(0, s.value)) / sq(FIELD_SCALE.valueMax));

  // Deterministic sampling of the un-highlighted baseline, so a real ~9k-SKU cohort never
  // renders more DOM nodes than a card can usefully show.
  const isCard = size === "card";
  const cap = MAX_BASELINE[size];
  const baseline = filtered.filter((s) => !colorById.has(s.id));
  const stride = Math.max(1, Math.ceil(baseline.length / cap));
  const sampledBaseline = baseline.filter((_, i) => i % stride === 0);
  const highlighted = filtered.filter((s) => colorById.has(s.id));
  const rendered = [...sampledBaseline, ...highlighted];

  // Drawn at the box's own pixel size — see `useChartSize` for why this isn't a fixed viewBox.
  const w = box.width;
  const h = box.height;
  const metrics = AXIS[size];
  const fontSize = metrics.font;
  const pad = { l: metrics.padL, r: metrics.padR, t: metrics.padT, b: metrics.padB };
  const gutter = { w: isCard ? 16 : 20, gap: isCard ? 6 : 8 };
  const plotL = pad.l + gutter.w + gutter.gap;
  const plotW = Math.max(1, w - plotL - pad.r);
  const plotH = Math.max(1, h - pad.t - pad.b);

  const px = (s: Sku) => {
    if (inGutter(s.vel, s.lastMovedQ)) {
      return pad.l + gutter.w * gutterFrac(s.id);
    }
    return plotL + (toLx(s.vel) / rxMax) * plotW;
  };
  const py = (s: Sku) => pad.t + (1 - fracOf(s)) * plotH;

  const r = isCard ? 1.8 : 2.6;

  // x axis: one-and-three per decade, positioned on the log scale, then thinned to whatever
  // this box can actually fit. Zero is deliberately NOT among them — zero velocity lives in the
  // gutter, which carries its own label, so a "0" tick would sit on top of that label and imply
  // the log axis reaches the origin (it can't).
  const xTickPx = (v: number) => plotL + (toLx(v) / rxMax) * plotW;
  const velocityTicks = spacedTicks(logTicks(velMax), xTickPx, fontSize * 3.4);
  const compact = plotW < WIDE_ENOUGH_FOR_FULL_LABELS;
  const gutterCount = filtered.filter((s) => inGutter(s.vel, s.lastMovedQ)).length;
  const xAxisLabel = compact ? "velocity (log)" : "velocity (boxes/mo, log scale)";

  // y axis: `fracOf` is a sqrt scale of a DIFFERENT unit per SKU type (value for Rx, inventory
  // for Dx). When the filter pins one type, the ticks can honestly show that type's own units
  // (inverting the sqrt: raw = frac² × max); mixed ("all"), a single absolute number would lie
  // for whichever type it wasn't computed from, so the ticks show the normalized fraction
  // instead — still meaningful ("this point sits at 75% of its own type's scale"), never wrong.
  // Tick COUNT follows the box, not the view size: a pin card dragged short has no more room
  // for five labels than a rail card does.
  const yFracTicks = plotH < 130 ? [0, 0.5, 1] : [0, 0.25, 0.5, 0.75, 1];
  const yAxisLabel =
    filters.type === "Dx" ? "inventory (units)" : filters.type === "Rx" ? "NTS value" : "% of scale"; // prettier-ignore
  const formatYTick = (frac: number): string => {
    if (filters.type === "Dx") {
      return Math.round(frac * frac * FIELD_SCALE.invMax).toLocaleString();
    }
    if (filters.type === "Rx") {
      return fmtMoneyShort(frac * frac * FIELD_SCALE.valueMax);
    }
    return `${Math.round(frac * 100)}%`;
  };

  function skuDetail(s: Sku) {
    return (
      <>
        <div className="max-w-40 truncate font-semibold">{s.name}</div>
        <div>
          {s.type} · {fmtVel(s.vel)} boxes/mo
        </div>
        <div>NTS value {fmtMoneyShort(s.value)}</div>
        <div>Inventory {s.inv.toLocaleString()} units</div>
        {colorById.has(s.id) ? <div className="italic">highlighted</div> : null}
      </>
    );
  }

  function handleMouseMove(e: MouseEvent<SVGSVGElement>) {
    const svg = svgRef.current;
    if (!svg || zoomControls.panning) {
      return;
    }
    // 1:1 with the drawing space — the SVG is sized in the same pixels it's drawn in.
    const rect = svg.getBoundingClientRect();
    const localX = e.clientX - rect.left;
    const localY = e.clientY - rect.top;
    const radius = HOVER_RADIUS[size];
    let bestId: number | null = null;
    let bestD = radius * radius;
    // Against the ZOOMED positions: the hit test has to agree with what's on screen, and
    // zooming into a dense cluster is precisely how a point becomes hoverable at all.
    for (const s of rendered) {
      const dx = zx(px(s)) - localX;
      const dy = zy(py(s)) - localY;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        bestId = s.id;
      }
    }
    setHoverId(bestId);
  }

  const hoveredSku = hoverId !== null ? rendered.find((s) => s.id === hoverId) : undefined;

  return (
    <div className="group/chart relative flex h-full min-h-[90px] min-w-0 flex-col gap-1">
      <div ref={box.ref} className="relative min-h-0 min-w-0 flex-1">
        <ZoomControls controls={zoomControls} />
        {w > 0 && h > 0 ? (
          <svg
            ref={svgRef}
            width={w}
            height={h}
            className={zoomControls.zoomed ? "cursor-grab touch-none" : undefined}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setHoverId(null)}
            {...zoomControls.svgProps}
          >
            {/* Everything inside the plot is drawn at its zoomed position but at its own,
                unscaled size — a 2px dot stays 2px, type stays type — and is clipped to the
                axes so a panned cluster can't wander into the labels. */}
            <defs>
              <clipPath id={clipId}>
                <rect x={pad.l} y={pad.t} width={w - pad.l - pad.r} height={plotH} />
              </clipPath>
            </defs>
            {/* y axis */}
            <line
              x1={pad.l}
              x2={pad.l}
              y1={pad.t}
              y2={pad.t + plotH}
              stroke="#141414"
              strokeWidth={1}
            />
            {yFracTicks.map((f) => {
              const y = zy(pad.t + (1 - f) * plotH);
              if (y < pad.t - 0.5 || y > pad.t + plotH + 0.5) {
                return null; // panned out of the plot
              }
              return (
                <g key={f}>
                  <line
                    x1={pad.l - 3}
                    x2={w - pad.r}
                    y1={y}
                    y2={y}
                    stroke="#E2E2E2"
                    strokeWidth={1}
                  />
                  <text
                    x={pad.l - 5}
                    y={y + fontSize / 3}
                    fontSize={fontSize}
                    textAnchor="end"
                    fill={AXIS_LABEL}
                  >
                    {formatYTick(f)}
                  </text>
                </g>
              );
            })}
            {/* Both axes are titled at both sizes: a tick value with no unit isn't a label. The
              y title is rotated into the left gutter, which costs width (already reserved by
              `padL`) rather than the scarcer height. */}
            <AxisTitle
              text={yAxisLabel}
              x={fontSize}
              y={pad.t + plotH / 2}
              fontSize={fontSize}
              rotate
            />
            {/* x axis: velocity ticks on the log-scaled main region */}
            <line
              x1={plotL}
              x2={w - pad.r}
              y1={h - pad.b}
              y2={h - pad.b}
              stroke="#141414"
              strokeWidth={1}
            />
            {velocityTicks.map((v) => {
              const x = zx(xTickPx(v));
              if (x < plotL - 0.5 || x > w - pad.r + 0.5) {
                return null; // panned out of the plot
              }
              return (
                <text
                  key={v}
                  x={x}
                  y={h - pad.b + fontSize + 3}
                  fontSize={fontSize}
                  textAnchor="middle"
                  fill={AXIS_LABEL}
                >
                  {fmtVel(v)}
                </text>
              );
            })}
            <AxisTitle
              text={xAxisLabel}
              x={plotL + plotW / 2}
              y={h - fontSize / 2 - 2}
              fontSize={fontSize}
            />
            {/* the zero-velocity / non-mover gutter, explicitly labelled and set off from the
              log axis by a dashed divider so it doesn't read as "just the low end of the scale" */}
            <line
              x1={zx(plotL - gutter.gap / 2)}
              x2={zx(plotL - gutter.gap / 2)}
              y1={pad.t}
              y2={h - pad.b}
              stroke="#C7C3BE"
              strokeDasharray="2 2"
              strokeWidth={1}
              clipPath={`url(#${clipId})`}
            />
            {/* Labelled with its own count, so the densest column on the chart says what it
                is and how big it is without a hover — the same treatment the interactive
                field gives it. Abbreviated where the box is too narrow to carry the words. */}
            <text
              x={pad.l + gutter.w / 2}
              y={h - pad.b + fontSize + 3}
              fontSize={fontSize}
              textAnchor="middle"
              fill={AXIS_LABEL}
            >
              {compact ? "none" : "no movement"}
            </text>
            {compact ? null : (
              <text
                x={pad.l + gutter.w / 2}
                y={h - pad.b + fontSize * 2 + 4}
                fontSize={fontSize}
                textAnchor="middle"
                fill={AXIS_LABEL}
              >
                {gutterCount.toLocaleString()} SKUs
              </text>
            )}
            <g clipPath={`url(#${clipId})`}>
              {sampledBaseline.map((s) => (
                <circle
                  key={s.id}
                  cx={zx(px(s))}
                  cy={zy(py(s))}
                  r={s.id === hoverId ? r + 1.5 : r}
                  fill={NEUTRAL}
                  opacity={s.id === hoverId ? 1 : 0.55}
                />
              ))}
              {highlighted.map((s) => (
                <circle
                  key={s.id}
                  cx={zx(px(s))}
                  cy={zy(py(s))}
                  r={s.id === hoverId ? r + 2.1 : r + 0.6}
                  fill={colorById.get(s.id)}
                />
              ))}
            </g>
          </svg>
        ) : null}
      </div>
      {hoveredSku ? (
        <ChartTooltip
          x={`${(zx(px(hoveredSku)) / w) * 100}%`}
          y={`${(zy(py(hoveredSku)) / h) * 100}%`}
        >
          {skuDetail(hoveredSku)}
        </ChartTooltip>
      ) : null}
      {chrome === "bare" ? null : (
        <>
          <p className="text-muted-foreground shrink-0 text-[11px]">
            {filters.type === "Dx" ? "inventory held" : "NTS value"} vs velocity (log) ·{" "}
            {highlighted.length.toLocaleString()} highlighted of {filtered.length.toLocaleString()}
          </p>
          {spec.lens ? (
            <p className="text-muted-foreground shrink-0 text-[11px] italic">
              Lens: {WL[spec.lens][1]} — open the full field at /velocity for the interactive
              breakdown.
            </p>
          ) : null}
        </>
      )}
    </div>
  );
}
